import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { 
  Car, 
  Fuel, 
  Settings2, 
  Calendar, 
  Gauge,
  ArrowLeft,
  Phone,
  Mail,
  Calculator,
  Check,
  MapPin,
  FileText,
  Shield,
  Zap
} from "lucide-react";
import FinancingCalculator from "@/components/FinancingCalculator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const VehicleDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [financingDialog, setFinancingDialog] = useState(false);

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const response = await axios.get(`${API}/vehicles/${id}`);
        setVehicle(response.data);
      } catch (err) {
        console.error("Error fetching vehicle:", err);
        setError("Fahrzeug nicht gefunden");
      } finally {
        setLoading(false);
      }
    };
    fetchVehicle();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center py-32">
          <div className="w-12 h-12 border-4 border-[#EFDF00] border-t-transparent animate-spin"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <Car className="w-16 h-16 text-[#D9D9D6] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-black mb-4">Fahrzeug nicht gefunden</h1>
          <p className="text-[#888B8D] mb-8">Das gewünschte Fahrzeug existiert nicht oder wurde entfernt.</p>
          <Link to="/fahrzeuge">
            <Button className="bg-black text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück zur Übersicht
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Back Navigation */}
      <div className="bg-[#F5F5F5] border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link 
            to="/fahrzeuge" 
            className="inline-flex items-center gap-2 text-[#888B8D] hover:text-black transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Zurück zur Fahrzeugübersicht</span>
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Left: Image */}
          <div className="relative">
            <div className="aspect-[4/3] bg-[#EBEBEB] overflow-hidden">
              {vehicle.image_url ? (
                <img 
                  src={vehicle.image_url} 
                  alt={`${vehicle.brand} ${vehicle.model}`}
                  className={`w-full h-full object-cover ${vehicle.is_sold ? 'grayscale' : ''}`}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Car className="w-24 h-24 text-[#D9D9D6]" />
                </div>
              )}
              
              {/* Badges */}
              <div className="absolute top-4 left-4 flex gap-2">
                {vehicle.is_sold ? (
                  <Badge className="bg-red-600 text-white text-sm px-4 py-2">Verkauft</Badge>
                ) : vehicle.is_new ? (
                  <Badge className="bg-[#EFDF00] text-black text-sm px-4 py-2">Neuwagen</Badge>
                ) : (
                  <Badge className="bg-black text-white text-sm px-4 py-2">Gebrauchtwagen</Badge>
                )}
              </div>

              {vehicle.is_sold && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="bg-red-600 text-white px-8 py-4 text-2xl font-bold">Verkauft</span>
                </div>
              )}
            </div>
          </div>

          {/* Right: Details */}
          <div>
            {/* Title & Price */}
            <div className="mb-6">
              <p className="text-[#888B8D] font-medium mb-1">{vehicle.brand}</p>
              <h1 className="text-3xl md:text-4xl font-bold text-black mb-2">
                {vehicle.model}
              </h1>
              {vehicle.boersentyp && (
                <p className="text-[#888B8D] text-sm">{vehicle.boersentyp}</p>
              )}
              
              {!vehicle.is_sold && (
                <div className="mt-4">
                  <span className="text-4xl font-bold text-black">
                    {vehicle.price.toLocaleString('de-DE')} €
                  </span>
                  <p className="text-sm text-[#888B8D] mt-1">inkl. MwSt.</p>
                </div>
              )}
            </div>

            {/* Key Specs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 p-4 bg-[#F5F5F5]">
              <div className="text-center">
                <Calendar className="w-5 h-5 mx-auto mb-1 text-[#888B8D]" />
                <p className="text-xs text-[#888B8D]">Erstzulassung</p>
                <p className="font-bold text-black">{vehicle.first_registration || vehicle.year}</p>
              </div>
              <div className="text-center">
                <Gauge className="w-5 h-5 mx-auto mb-1 text-[#888B8D]" />
                <p className="text-xs text-[#888B8D]">Kilometerstand</p>
                <p className="font-bold text-black">{vehicle.mileage.toLocaleString('de-DE')} km</p>
              </div>
              <div className="text-center">
                <Fuel className="w-5 h-5 mx-auto mb-1 text-[#888B8D]" />
                <p className="text-xs text-[#888B8D]">Kraftstoff</p>
                <p className="font-bold text-black">{vehicle.fuel_type}</p>
              </div>
              <div className="text-center">
                <Settings2 className="w-5 h-5 mx-auto mb-1 text-[#888B8D]" />
                <p className="text-xs text-[#888B8D]">Getriebe</p>
                <p className="font-bold text-black">{vehicle.transmission}</p>
              </div>
            </div>

            {/* Action Buttons */}
            {!vehicle.is_sold ? (
              <div className="space-y-3 mb-8">
                <Link 
                  to="/kontakt" 
                  state={{ 
                    vehicle: {
                      id: vehicle.id,
                      brand: vehicle.brand,
                      model: vehicle.model,
                      price: vehicle.price,
                      year: vehicle.year,
                      image_url: vehicle.image_url
                    }
                  }}
                >
                  <Button className="w-full bg-[#EFDF00] text-black hover:bg-[#D9C900] font-bold py-6 text-lg">
                    <Mail className="w-5 h-5 mr-2" />
                    Jetzt anfragen
                  </Button>
                </Link>
                
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    variant="outline"
                    className="border-black text-black hover:bg-black hover:text-white py-6"
                    onClick={() => setFinancingDialog(true)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Rate berechnen
                  </Button>
                  <a href="tel:+499682722">
                    <Button 
                      variant="outline"
                      className="w-full border-black text-black hover:bg-black hover:text-white py-6"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Anrufen
                    </Button>
                  </a>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 border border-red-200 p-4 mb-8">
                <p className="text-red-600 font-bold">Dieses Fahrzeug wurde bereits verkauft.</p>
                <Link to="/fahrzeuge" className="text-red-600 underline text-sm">
                  Weitere Fahrzeuge ansehen
                </Link>
              </div>
            )}

            {/* Dealer Info */}
            <div className="bg-black text-white p-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#EFDF00] mt-0.5" />
                <div>
                  <p className="font-bold">Auto Thurm</p>
                  <p className="text-sm text-[#D9D9D6]">Wildenreuth A 55, 92681 Erbendorf</p>
                  <p className="text-sm text-[#D9D9D6]">Tel: 09682 722</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Technical Details */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Technische Daten */}
          <div>
            <h2 className="text-2xl font-bold text-black mb-6 flex items-center gap-2">
              <FileText className="w-6 h-6 text-[#EFDF00]" />
              Technische Daten
            </h2>
            <div className="border">
              <table className="w-full">
                <tbody>
                  {vehicle.vin && (
                    <tr className="border-b">
                      <td className="px-4 py-3 bg-[#F5F5F5] font-medium w-1/2">Fahrzeug-Ident-Nr. (FIN)</td>
                      <td className="px-4 py-3 font-mono text-sm">{vehicle.vin}</td>
                    </tr>
                  )}
                  {(vehicle.hsn || vehicle.tsn) && (
                    <tr className="border-b">
                      <td className="px-4 py-3 bg-[#F5F5F5] font-medium">HSN/TSN</td>
                      <td className="px-4 py-3">{vehicle.hsn}/{vehicle.tsn}</td>
                    </tr>
                  )}
                  <tr className="border-b">
                    <td className="px-4 py-3 bg-[#F5F5F5] font-medium">Erstzulassung</td>
                    <td className="px-4 py-3">{vehicle.first_registration || vehicle.year}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 bg-[#F5F5F5] font-medium">Kilometerstand</td>
                    <td className="px-4 py-3">{vehicle.mileage.toLocaleString('de-DE')} km</td>
                  </tr>
                  {vehicle.previous_owners > 0 && (
                    <tr className="border-b">
                      <td className="px-4 py-3 bg-[#F5F5F5] font-medium">Vorbesitzer</td>
                      <td className="px-4 py-3">{vehicle.previous_owners}</td>
                    </tr>
                  )}
                  {vehicle.next_inspection && (
                    <tr className="border-b">
                      <td className="px-4 py-3 bg-[#F5F5F5] font-medium">Nächste HU</td>
                      <td className="px-4 py-3">{vehicle.next_inspection}</td>
                    </tr>
                  )}
                  <tr className="border-b">
                    <td className="px-4 py-3 bg-[#F5F5F5] font-medium">Farbe</td>
                    <td className="px-4 py-3">{vehicle.color || "Nicht angegeben"}</td>
                  </tr>
                  {vehicle.vehicle_type && (
                    <tr className="border-b">
                      <td className="px-4 py-3 bg-[#F5F5F5] font-medium">Fahrzeugtyp</td>
                      <td className="px-4 py-3">{vehicle.vehicle_type}</td>
                    </tr>
                  )}
                  {vehicle.doors > 0 && (
                    <tr className="border-b">
                      <td className="px-4 py-3 bg-[#F5F5F5] font-medium">Türen</td>
                      <td className="px-4 py-3">{vehicle.doors}</td>
                    </tr>
                  )}
                  {vehicle.seats > 0 && (
                    <tr className="border-b">
                      <td className="px-4 py-3 bg-[#F5F5F5] font-medium">Sitzplätze</td>
                      <td className="px-4 py-3">{vehicle.seats}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Motor & Umwelt */}
          <div>
            <h2 className="text-2xl font-bold text-black mb-6 flex items-center gap-2">
              <Zap className="w-6 h-6 text-[#EFDF00]" />
              Motor & Umwelt
            </h2>
            <div className="border">
              <table className="w-full">
                <tbody>
                  <tr className="border-b">
                    <td className="px-4 py-3 bg-[#F5F5F5] font-medium w-1/2">Kraftstoff</td>
                    <td className="px-4 py-3">{vehicle.fuel_type}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 bg-[#F5F5F5] font-medium">Getriebe</td>
                    <td className="px-4 py-3">{vehicle.transmission}</td>
                  </tr>
                  {vehicle.engine_power_kw > 0 && (
                    <tr className="border-b">
                      <td className="px-4 py-3 bg-[#F5F5F5] font-medium">Leistung</td>
                      <td className="px-4 py-3">{vehicle.engine_power_kw} kW ({vehicle.engine_power_ps || Math.round(vehicle.engine_power_kw * 1.36)} PS)</td>
                    </tr>
                  )}
                  {vehicle.engine_displacement > 0 && (
                    <tr className="border-b">
                      <td className="px-4 py-3 bg-[#F5F5F5] font-medium">Hubraum</td>
                      <td className="px-4 py-3">{vehicle.engine_displacement} ccm</td>
                    </tr>
                  )}
                  {vehicle.drive_type && (
                    <tr className="border-b">
                      <td className="px-4 py-3 bg-[#F5F5F5] font-medium">Antrieb</td>
                      <td className="px-4 py-3">{vehicle.drive_type}</td>
                    </tr>
                  )}
                  {vehicle.emission_class && (
                    <tr className="border-b">
                      <td className="px-4 py-3 bg-[#F5F5F5] font-medium">Schadstoffklasse</td>
                      <td className="px-4 py-3">{vehicle.emission_class}</td>
                    </tr>
                  )}
                  {vehicle.co2_emission > 0 && (
                    <tr className="border-b">
                      <td className="px-4 py-3 bg-[#F5F5F5] font-medium">CO₂-Emission</td>
                      <td className="px-4 py-3">{vehicle.co2_emission} g/km</td>
                    </tr>
                  )}
                  {vehicle.fuel_consumption > 0 && (
                    <tr className="border-b">
                      <td className="px-4 py-3 bg-[#F5F5F5] font-medium">Verbrauch (komb.)</td>
                      <td className="px-4 py-3">{vehicle.fuel_consumption} l/100km</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Ausstattung */}
        {vehicle.features && vehicle.features.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-black mb-6 flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#EFDF00]" />
              Ausstattung ({vehicle.features.length} Merkmale)
            </h2>
            <div className="bg-[#F5F5F5] p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {vehicle.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 bg-white px-3 py-2">
                    <Check className="w-4 h-4 text-green-600 shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Financing Dialog */}
      <Dialog open={financingDialog} onOpenChange={setFinancingDialog}>
        <DialogContent className="max-w-md p-0 bg-transparent border-0">
          <FinancingCalculator 
            vehiclePrice={vehicle?.price || 0}
            vehicleName={vehicle ? `${vehicle.brand} ${vehicle.model} (${vehicle.year})` : ""}
          />
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default VehicleDetailPage;
